import React from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import Typography from '@material-ui/core/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import CardActionArea from '@mui/material/CardActionArea';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import DescriptionIcon from '@mui/icons-material/Description';
import './styles/blog.css';

export const Blog = ({ darkMode }) => {

  const papers = [
    {
      title: "Martingale Analysis & Monte Carlo Simulation",
      description: "Statistical analysis of the Martingale betting strategy using Monte Carlo simulation. Explores expected outcomes, probability distributions, and the mathematical limits of doubling strategies through extensive simulation runs.",
      course: "CS 7646 — ML for Trading",
      date: "Fall 2025",
      tech: ["Monte Carlo", "Statistics", "Probability", "Python"],
      pdf: process.env.PUBLIC_URL + "/papers/p1_martingale_report.pdf",
    },
    {
      title: "Portfolio Optimization & Sharpe Ratio",
      description: "Analysis of portfolio optimization techniques to maximize risk-adjusted returns. Evaluates optimal asset allocation strategies using daily return statistics, volatility measures, and Sharpe Ratio as the objective function.",
      course: "CS 7646 — ML for Trading",
      date: "Fall 2025",
      tech: ["Optimization", "Sharpe Ratio", "Portfolio Theory", "Python"],
      pdf: process.env.PUBLIC_URL + "/papers/p2_optimization_report.pdf",
    },
    {
      title: "CART Regression & Ensemble Methods",
      description: "Evaluation of CART-based regression algorithms on the Istanbul Stock Exchange dataset. Compares Decision Trees, Random Trees, and Bootstrap Aggregation (Bagging) to analyze the bias-variance tradeoff and overfitting behavior across different leaf sizes.",
      course: "CS 7646 — ML for Trading",
      date: "Fall 2025",
      tech: ["Decision Trees", "Bagging", "Bias-Variance", "Regression"],
      pdf: process.env.PUBLIC_URL + "/papers/p3_assesslearners_report.pdf",
    },
    {
      title: "Technical Indicators & Theoretically Optimal Strategy",
      description: "Comprehensive evaluation of five technical trading indicators — Bollinger Band Percentage, CCI, MACD, RSI, and Momentum — applied to JPM stock. Includes a Theoretically Optimal Strategy benchmark using perfect foresight.",
      course: "CS 7646 — ML for Trading",
      date: "Fall 2025",
      tech: ["BBP", "RSI", "MACD", "CCI", "Momentum", "Trading"],
      pdf: process.env.PUBLIC_URL + "/papers/p6_indicatorsTOS_report.pdf",
    },
    {
      title: "Strategy Evaluation: Manual vs. ML Trading",
      description: "Comparative study of a hand-crafted Manual Strategy versus a machine learning Strategy Learner for JPM stock trading. Analyzes in-sample and out-of-sample performance, and how market impact affects trading behavior and returns.",
      course: "CS 7646 — ML for Trading",
      date: "Fall 2025",
      tech: ["ML Strategy", "Bagged Random Trees", "Backtesting", "Market Impact"],
      pdf: process.env.PUBLIC_URL + "/papers/p8_strategyEval_report.pdf",
    },
  ];

  return (
    <Container className={darkMode ? 'container-dark' : 'container-light'}>

      <Box className={darkMode ? 'box-dark' : 'box-light'} sx={{ pt: 4, pb: 2 }}>
        <Typography variant="h5" className="box-about-text" style={{ textAlign: 'center' }}>
          Research papers and reports from my graduate coursework at Georgia Tech.
        </Typography>
      </Box>

      <Divider className={darkMode ? 'divider-dark' : 'divider-light'} />

      <Box className="papers-list">
        {papers.map((paper, index) => (
          <Card key={index} className={darkMode ? 'paper-card-dark' : 'paper-card-light'}>
            <CardActionArea
              component="a"
              href={paper.pdf}
              target="_blank"
              rel="noopener noreferrer"
            >
              <CardContent className="paper-card-content">
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <DescriptionIcon className={darkMode ? 'paper-icon-dark' : 'paper-icon-light'} sx={{ fontSize: 40, mt: 0.5, flexShrink: 0 }} />
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" component="div" className="paper-title">
                      {paper.title}
                    </Typography>
                    <Typography variant="caption" className={darkMode ? 'category-dark' : 'category-light'}>
                      {paper.course} — {paper.date}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" className={darkMode ? 'card-text-dark' : ''} sx={{ mt: 1 }}>
                      {paper.description}
                    </Typography>
                    <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                      {paper.tech.map((t, i) => (
                        <Chip
                          key={i}
                          label={t}
                          size="small"
                          className={darkMode ? 'chip-dark' : 'chip-light'}
                        />
                      ))}
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </CardActionArea>
            <CardActions sx={{ pl: 9 }}>
              <Button
                size="small"
                color="primary"
                href={paper.pdf}
                target="_blank"
                rel="noopener noreferrer"
              >
                VIEW PDF
              </Button>
            </CardActions>
          </Card>
        ))}
      </Box>

    </Container>
  );
};
